import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { badgeStyle } from '../Components/MonitoringUi';

// ─── Feature request modal ────────────────────────────────────────────────────

const FEATURES = [
  { id: 'auto_deploy',     icon: '⚡', label: 'Auto-deploy on every PR',            desc: 'Spin up a full isolated env in your AWS account the moment a PR is opened' },
  { id: 'auto_destroy',    icon: '🧹', label: 'Auto-destroy when PR closes',         desc: 'Zero lingering resources — environments clean up themselves on merge or close' },
  { id: 'shareable_url',   icon: '🔗', label: 'Shareable preview URL per PR',        desc: 'One-click link to share with designers, PMs, and stakeholders for review' },
  { id: 'env_vars',        icon: '🔐', label: 'Per-env secrets & env vars',          desc: 'Inject staging credentials automatically — no manual config per branch' },
  { id: 'pr_comment',      icon: '💬', label: 'GitHub PR comment with preview link', desc: 'Bot drops the live URL directly into the PR thread — no dashboard needed' },
  { id: 'db_seeding',      icon: '🗄️', label: 'DB snapshot seeding',               desc: 'Each preview env starts with a sanitized copy of production data' },
  { id: 'cost_cap',        icon: '💰', label: 'Per-env cost cap & auto-shutdown',    desc: 'Set a daily spend limit — idle preview envs shut down to save money' },
  { id: 'multi_service',   icon: '🧩', label: 'Multi-service / monorepo support',   desc: 'Deploy multiple services together per PR, not just the frontend or just the API' },
  { id: 'slack_notify',    icon: '📱', label: 'Slack notification when env is ready', desc: 'Ping the author and reviewers the moment the preview URL is live' },
  { id: 'custom_domain',   icon: '🌐', label: 'Custom subdomain per PR',             desc: 'pr-47.preview.yourapp.com instead of a random URL' },
];

const FeatureRequestModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const toggle = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSubmit = () => {
    const body = [
      'Hi QixDeploy team,',
      '',
      'I\'d love to see these Preview Environment features prioritized:',
      ...Array.from(selected).map(id => {
        const f = FEATURES.find(f => f.id === id);
        return f ? `  • ${f.icon} ${f.label}` : '';
      }),
      '',
      'Happy to jump on a call to give more context — please reach out!',
    ].join('\n');
    const subject = encodeURIComponent('Feature request: Preview Environments');
    window.location.href = `mailto:product@qixdeploy.com?subject=${subject}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,5,8,.78)' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '14px', padding: '24px', width: '480px', maxWidth: '92vw', maxHeight: '82vh', overflowY: 'auto', fontFamily: "'Inter',sans-serif" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5', marginBottom: '4px' }}>
          Which preview features matter most to you?
        </div>
        <div style={{ fontSize: '11.5px', color: '#52525f', marginBottom: '18px' }}>
          Select everything you care about — we rank features by demand and ship the highest-voted ones first.
        </div>

        {!submitted ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
              {FEATURES.map(f => (
                <label
                  key={f.id}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', background: selected.has(f.id) ? 'rgba(99,102,241,.08)' : '#17171e', border: `1px solid ${selected.has(f.id) ? 'rgba(99,102,241,.3)' : '#26262f'}`, borderRadius: '8px', cursor: 'pointer', transition: 'all .15s' }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(f.id)}
                    onChange={() => toggle(f.id)}
                    style={{ marginTop: '2px', accentColor: '#6366f1', flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: '12.5px', fontWeight: 500, color: '#eeeef5', marginBottom: '2px' }}>{f.icon} {f.label}</div>
                    <div style={{ fontSize: '10.5px', color: '#52525f' }}>{f.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer', border: '1px solid #32323d', background: '#17171e', color: '#eeeef5' }}>
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={selected.size === 0}
                style={{ padding: '8px 18px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 600, cursor: selected.size === 0 ? 'not-allowed' : 'pointer', border: 'none', background: selected.size === 0 ? '#3d3f6b' : '#6366f1', color: '#fff', opacity: selected.size === 0 ? 0.6 : 1 }}
              >
                📧 Send{selected.size > 0 ? ` (${selected.size} selected)` : ''} →
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center' as const, padding: '24px 0' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🙏</div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#eeeef5', marginBottom: '6px' }}>Thanks for the input!</div>
            <div style={{ fontSize: '12px', color: '#8e8ea8', marginBottom: '18px' }}>Your email client should open with the details pre-filled. We read every one of these.</div>
            <button onClick={onClose} style={{ padding: '8px 22px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer', border: 'none', background: '#6366f1', color: '#fff' }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────────

export const PreviewEnvironmentsView: React.FC = () => {
  const navigate = useNavigate();
  const [exploring, setExploring] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const badgePill = (variant: 'ok' | 'warn' = 'ok'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600,
    ...(variant === 'ok'
      ? { background: 'rgba(34,197,94,.1)', color: '#22c55e' }
      : { background: 'rgba(234,179,8,.1)',  color: '#eab308' }),
  });

  const envs = [
    { branch: 'feat/auth-redesign',      pr: 'PR #47 · opened 2h ago by @ali',  url: 'preview-pr47.qixdeploy.app', status: 'ok'   as const, label: 'Live',     cpu: '12%', mem: '34%', cost: '$0.18 today' },
    { branch: 'fix/db-connection-pool',  pr: 'PR #51 · opened 5h ago by @john', url: 'preview-pr51.qixdeploy.app', status: 'warn' as const, label: 'Building', cpu: '—',   mem: '—',   cost: '—'           },
    { branch: 'feat/team-invites',       pr: 'PR #55 · opened 1h ago by @sara', url: 'preview-pr55.qixdeploy.app', status: 'ok'   as const, label: 'Live',     cpu: '8%',  mem: '29%', cost: '$0.07 today' },
  ];

  const steps = [
    { icon: '🔀', title: 'Open a PR',    desc: 'Push any branch and open a pull request on GitHub, GitLab, or Bitbucket' },
    { icon: '⚡', title: 'Auto-Deploy',  desc: 'QixDeploy spins up a full isolated environment in your AWS account in ~3 minutes' },
    { icon: '🔗', title: 'Share & Test', desc: 'Share the preview URL with designers, PMs, or stakeholders for sign-off' },
    { icon: '✅', title: 'Merge',        desc: 'Environment auto-destroys the moment the PR is merged or closed — zero cleanup' },
  ];

  const painPoints = [
    { icon: '😤', title: '"Works on my machine"', desc: 'Reviewers can\'t test the actual change — they trust screenshots and hope for the best' },
    { icon: '😰', title: 'Shared staging is always broken', desc: 'One team\'s broken deploy blocks everyone else from testing anything' },
    { icon: '💸', title: 'Manual QA environments cost a fortune', desc: 'Dedicated QA envs run 24/7 even when nobody is using them' },
    { icon: '🐢', title: 'Review cycles take days', desc: 'Back-and-forth over Figma links and Loom videos instead of just clicking through the real thing' },
    { icon: '🔥', title: 'Bugs ship because reviewers can\'t catch them', desc: 'Code review alone misses interaction bugs, mobile layout breaks, and edge-case flows' },
    { icon: '🔑', title: 'Sharing staging credentials is a security risk', desc: 'Everyone uses the same shared staging login — no audit trail, no isolation' },
  ];

  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>
      {showModal && <FeatureRequestModal onClose={() => setShowModal(false)} />}

      {/* Topbar */}
      <div style={{ height: '50px', background: '#111116', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', padding: '0 20px', position: 'sticky' as const, top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' }}>
          <span>Home</span><span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>Preview Environments</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          {exploring && (
            <button
              onClick={() => setShowModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', border: '1px solid rgba(99,102,241,.25)', background: 'transparent', color: '#818cf8' }}
            >
              📧 Request feature
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '20px 24px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>Preview Environments</h1>
          <p style={{ fontSize: '13px', color: '#8e8ea8' }}>Every Pull Request gets its own live environment. Share, test, and merge with confidence.</p>
        </div>

        {/* Content area */}
        <div style={{ position: 'relative' }}>

          {/* ── Coming soon overlay ─────────────────────────────────────────── */}
          {!exploring && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '40px', pointerEvents: 'none' }}>
              <div style={{
                pointerEvents: 'auto',
                background: 'rgba(9,9,14,0.88)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(99,102,241,.3)',
                borderRadius: '16px',
                padding: '28px 36px',
                maxWidth: '460px',
                width: '90%',
                textAlign: 'center' as const,
                boxShadow: '0 16px 60px rgba(0,0,0,.7)',
              }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🚀</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '17px', fontWeight: 700, color: '#eeeef5', marginBottom: '8px' }}>
                  Preview Environments — Coming Soon
                </div>
                <div style={{ fontSize: '12px', color: '#8e8ea8', lineHeight: 1.75, marginBottom: '22px' }}>
                  Per-PR isolated environments on your own AWS account — deployed automatically, destroyed automatically.{' '}
                  <strong style={{ color: '#c7d2fe' }}>Everything behind this overlay is a preview of exactly what you'll get.</strong>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' as const }}>
                  <button
                    onClick={() => setExploring(true)}
                    style={{ padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#6366f1', color: '#fff' }}
                  >
                    Explore Preview →
                  </button>
                  <button
                    onClick={() => setShowModal(true)}
                    style={{ padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(99,102,241,.3)', background: 'transparent', color: '#a5b4fc' }}
                  >
                    📧 Request Feature
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Preview mode bar ─────────────────────────────────────────────── */}
          {exploring && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(99,102,241,.07)', border: '1px solid rgba(99,102,241,.18)', borderRadius: '8px', padding: '8px 14px', marginBottom: '16px', fontSize: '11.5px', color: '#a5b4fc' }}>
              <span>👁️ <strong>Preview mode</strong> — sample data, no real environments are running</span>
              <button
                onClick={() => setShowModal(true)}
                style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(99,102,241,.25)', background: 'transparent', color: '#818cf8' }}
              >
                📧 Request this feature
              </button>
            </div>
          )}

          {/* ── Feature content ───────────────────────────────────────────────── */}
          <div style={{ opacity: exploring ? 1 : 0.28, filter: exploring ? 'none' : 'blur(0.8px)', transition: 'opacity .3s, filter .3s', pointerEvents: exploring ? 'auto' : 'none', userSelect: exploring ? 'auto' : 'none' }}>

            {/* Why this matters — pain point grid */}
            <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '18px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '4px' }}>Why teams need this</div>
              <div style={{ fontSize: '12px', color: '#52525f', marginBottom: '14px' }}>The 6 review-cycle problems preview environments eliminate</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '10px' }}>
                {painPoints.map(p => (
                  <div key={p.title} style={{ background: '#17171e', border: '1px solid #26262f', borderRadius: '8px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '16px', marginBottom: '6px' }}>{p.icon}</div>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '3px' }}>{p.title}</div>
                    <div style={{ fontSize: '11px', color: '#52525f', lineHeight: 1.6 }}>{p.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active PR environments */}
            <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '18px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '2px' }}>Active PR Environments</div>
                  <div style={{ fontSize: '11.5px', color: '#52525f' }}>Auto-created · auto-destroyed · billed per minute</div>
                </div>
                <span style={badgePill('ok')}>
                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#22c55e' }}/>
                  2 Live · 1 Building
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {envs.map(env => (
                  <div key={env.branch} style={{ background: '#17171e', border: '1px solid #26262f', borderRadius: '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg,rgba(99,102,241,.2),rgba(168,85,247,.15))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>🟣</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#eeeef5', fontFamily: "'IBM Plex Mono',monospace", marginBottom: '2px' }}>{env.branch}</div>
                      <div style={{ fontSize: '11px', color: '#8e8ea8' }}>{env.pr}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#52525f' }}>
                      <span>CPU <span style={{ color: '#8e8ea8' }}>{env.cpu}</span></span>
                      <span>Mem <span style={{ color: '#8e8ea8' }}>{env.mem}</span></span>
                      <span>Cost <span style={{ color: '#22c55e' }}>{env.cost}</span></span>
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#818cf8' }}>{env.url} ↗</div>
                    <span style={{ ...badgePill(env.status), display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: env.status === 'ok' ? '#22c55e' : '#eab308' }}/>
                      {env.label}
                    </span>
                    <div style={{ width: '26px', height: '26px', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#52525f', cursor: 'pointer' }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M2 3h8M4.5 3V2h3v1M5 5.5v4M7 5.5v4M2.5 3l.6 7h5.8l.6-7"/>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How it works + stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '14px', marginBottom: '16px' }}>

              <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '18px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '16px' }}>How it works</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {steps.map((step, i) => (
                    <div key={step.title} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>
                        {step.icon}
                      </div>
                      <div>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '2px' }}>
                          <span style={{ color: '#52525f', marginRight: '6px', fontSize: '11px' }}>{i + 1}.</span>{step.title}
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#52525f', lineHeight: 1.6 }}>{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5' }}>At a glance</div>
                {[
                  { label: 'Avg env spin-up time',  value: '~3 min',    color: '#22c55e' },
                  { label: 'Avg cost per PR env',   value: '$0.40/day', color: '#818cf8' },
                  { label: 'Auto-destroy on close', value: 'Yes',       color: '#22c55e' },
                  { label: 'Supports',              value: 'AWS · Azure', color: '#8e8ea8' },
                  { label: 'DB seeding',            value: 'Coming soon', color: '#eab308' },
                  { label: 'Slack notifications',   value: 'Coming soon', color: '#eab308' },
                ].map(stat => (
                  <div key={stat.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid #1a1a22' }}>
                    <span style={{ fontSize: '11.5px', color: '#52525f' }}>{stat.label}</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: stat.color }}>{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA strip */}
            <div style={{ background: 'linear-gradient(135deg,rgba(99,102,241,.08),rgba(168,85,247,.06))', border: '1px solid rgba(99,102,241,.2)', borderRadius: '10px', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' as const }}>
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '3px' }}>Be the first to know when this ships</div>
                <div style={{ fontSize: '12px', color: '#8e8ea8' }}>Tell us which features matter most — your votes shape the roadmap</div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowModal(true)}
                  style={{ padding: '9px 18px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#6366f1', color: '#fff' }}
                >
                  📧 Vote on features →
                </button>
                <button
                  onClick={() => navigate('/plans')}
                  style={{ padding: '9px 18px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(99,102,241,.25)', background: 'transparent', color: '#a5b4fc' }}
                >
                  View plans ↗
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewEnvironmentsView;